import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../components/AdminLayout';
import { STATUS_COLORS, PIPELINE_STAGE_OPTIONS, getStageIndex, HOME_SHOPPING_STAGES, POST_ACCEPTANCE_STAGES } from '../constants/statusColors';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const formatTimeline = (val) => {
  if (!val) return '—';
  const v = String(val).toLowerCase().trim();
  if (v.includes('asap') || v.includes('immediately') || v === 'now') return 'ASAP';
  if (v.includes('1-3') || v.includes('1 to 3') || v.includes('1–3')) return '1–3 months';
  if (v.includes('3-6') || v.includes('3 to 6') || v.includes('3–6')) return '3–6 months';
  if (v.includes('6-12') || v.includes('6 to 12') || v.includes('6–12')) return '6–12 months';
  if (v.includes('12') || v.includes('year') || v.includes('1 yr')) return '12+ months';
  if (v.includes('just') || v.includes('browsing') || v.includes('exploring')) return 'Just browsing';
  if (v.includes('1') && v.includes('month')) return '< 1 month';
  return val;
};

// Stage aliases for count matching (matches BORROWER_PHASE_MAP on backend)
const STAGE_ALIASES = {
  new: ['new', 'not_started', 'pre_qualified', 'started', 'registered'],
  submitted: ['submitted', 'applied'],
  approved: ['approved', 'conditional_approval'],
  home_shopping: ['home_shopping'],
  offer_requested: ['offer_requested'],
  offer_submitted: ['offer_submitted'],
  option_period: ['option_period'],
  closing: ['closing', 'clear_to_close'],
  closed: ['closed', 'funded'],
};

function StatusBadge({ status }) {
  const s = STATUS_COLORS[status] || { label: status?.replace(/_/g, ' ') || '—', bg: '#f9fafb', text: '#374151' };
  return (
    <span style={{ fontSize: '11px', fontWeight: '600', padding: '3px 9px', borderRadius: '9999px', backgroundColor: s.bg, color: s.text, whiteSpace: 'nowrap' }}>
      {s.label}
    </span>
  );
}

export default function AdminBorrowers() {
  const navigate = useNavigate();
  const token = localStorage.getItem('doorly_admin_token');
  const [selectedStages, setSelectedStages] = useState([]);
  const [search, setSearch] = useState('');
  const [borrowers, setBorrowers] = useState([]);
  // Pending partner referrals — buyer_referrals rows where the
  // invited buyer hasn't registered yet. Surfaced in a compact
  // section above the main borrowers table so admins see referrals
  // immediately after the email alert lands, not after the borrower
  // happens to click their invite. Empty array if none.
  // Collapsed by default — pending invites are useful to keep on hand
  // but they shouldn't crowd the pipeline view. Header row stays
  // visible with the count so the admin can see at-a-glance how many
  // partner referrals are waiting; clicking expands to the rows + Resend.
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState('last_seen_at');
  const [sortDir, setSortDir] = useState('desc');
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [partnerFilter, setPartnerFilter] = useState('');
  const [partnersList, setPartnersList] = useState([]);
  const PAGE_SIZE = 25;   // rows shown per page in the table
  const FETCH_LIMIT = 500; // fetch all at once for accurate client-side counts

  // Pipeline cog
  const [showPipelineCog, setShowPipelineCog] = useState(false);
  const [showArchivedModal, setShowArchivedModal] = useState(false);
  // Test accounts (App Store / Play reviewer logins, internal rows) are hidden
  // by default so the Pipeline shows customers. The toggle is deliberately not
  // persisted — it is a "let me check something" switch, and a sticky one would
  // leave someone looking at an inflated pipeline weeks later without knowing.
  const [showTestAccounts, setShowTestAccounts] = useState(false);
  const [showAdversedModal, setShowAdversedModal] = useState(false);
  const [archivedLoans, setArchivedLoans] = useState([]);
  const [adversedLoans, setAdversedLoans] = useState([]);
  const pipelineCogRef = useRef(null);

  // Stage-pills horizontal scroll: show a right-edge fade while there's more to
  // scroll (signals the row is swipeable on mobile); hide it once at the end.
  const chipsRef = useRef(null);
  const [chipsFade, setChipsFade] = useState(false);
  const updateChipsFade = () => {
    const el = chipsRef.current;
    if (!el) return;
    setChipsFade(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  };

  useEffect(() => {
    const handler = (e) => { if (pipelineCogRef.current && !pipelineCogRef.current.contains(e.target)) setShowPipelineCog(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Re-measure the fade after the pills render and on resize.
  useEffect(() => {
    updateChipsFade();
    window.addEventListener('resize', updateChipsFade);
    return () => window.removeEventListener('resize', updateChipsFade);
  }, [borrowers.length, selectedStages.length]);

  const handleStageToggle = (stageValue) => {
    setSelectedStages(prev =>
      prev.includes(stageValue)
        ? prev.filter(s => s !== stageValue)
        : [...prev, stageValue]
    );
    setPage(1);
  };

  const handleClearStages = () => {
    setSelectedStages([]);
    setPage(1);
  };

  const fetchBorrowers = useCallback(async () => {
    if (!token) {
      setLoading(false);
      navigate('/admin/login');
      return;
    }
    setLoading(true);
    setFetchError(null);
    try {
      const backendSort = sortBy === 'application_status' ? 'last_seen_at' : sortBy;
      const params = { limit: FETCH_LIMIT, sort: backendSort, dir: sortDir };
      if (search) params.search = search;
      if (showTestAccounts) params.include_test = '1';
      const reqHeaders = { Authorization: `Bearer ${token}` };
      // Partner id filter goes in a header, not the query string (log hygiene).
      if (partnerFilter) reqHeaders['X-Partner-Id'] = partnerFilter;
      const res = await axios.get(`${API}/api/admin/borrowers`, {
        headers: reqHeaders,
        params,
      });
      let data = res.data.borrowers || [];
      if (sortBy === 'application_status') {
        data = [...data].sort((a, b) => {
          const idxA = getStageIndex(a.application_status);
          const idxB = getStageIndex(b.application_status);
          return sortDir === 'asc' ? idxA - idxB : idxB - idxA;
        });
      }
      setBorrowers(data);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem('doorly_admin_token');
        localStorage.removeItem('doorly_admin_user');
        navigate('/admin/login');
        return;
      }
      setFetchError(err?.response?.data?.error || err?.message || 'Failed to load borrowers');
    }
    setLoading(false);
  }, [token, search, sortBy, sortDir, partnerFilter, showTestAccounts, navigate]);

  useEffect(() => { fetchBorrowers(); }, [fetchBorrowers]);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/api/admin/partners/list`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setPartnersList(r.data.partners || []))
      .catch(() => {});
  }, [token]);

  const handleSort = (col) => {
    if (sortBy === col) setSortDir((d) => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortDir('asc'); }
    setPage(1);
  };

  const filteredBorrowers = useMemo(() => {
    if (selectedStages.length === 0) return borrowers;
    const allowed = new Set(selectedStages.flatMap(s => STAGE_ALIASES[s] || [s]));
    return borrowers.filter(b => allowed.has(b.application_status));
  }, [borrowers, selectedStages]);

  const totalPages = Math.ceil(filteredBorrowers.length / PAGE_SIZE);

  const SortIcon = ({ col }) => {
    if (sortBy !== col) return <span style={{ color: '#d1d5db' }}> ↕</span>;
    return <span style={{ color: '#3a6ad9' }}>{sortDir === 'asc' ? ' ↑' : ' ↓'}</span>;
  };

  const totalVolume = filteredBorrowers.reduce((sum, b) => {
    const s = b.application_status;
    let price;
    if (POST_ACCEPTANCE_STAGES.includes(s)) {
      price = parseFloat(b.contract_purchase_price || b.doorly_purchase_price || b.purchase_price || 0);
    } else if (HOME_SHOPPING_STAGES.includes(s) || (['approved','conditional_approval'].includes(s) && b.smartestimate_accepted)) {
      const dp = parseFloat(b.doorly_purchase_price || 0);
      // D3 (2026-11-01): no resale factor — the price is the price.
      price = dp > 0 ? Math.round(dp) : parseFloat(b.atlas_max_purchase_price || b.purchase_price || 0);
    } else if (['approved','conditional_approval'].includes(s) && b.atlas_price_locked) {
      price = parseFloat(b.atlas_max_purchase_price || b.max_approval_amount || b.purchase_price || 0);
    } else {
      price = parseFloat(b.purchase_price || 0);
    }
    return sum + price;
  }, 0);

  const stateColors = {
    TX: { bg: '#dbeafe', color: '#2854c0' },
    FL: { bg: '#fef3c7', color: '#7a5200' },
  };

  const normalizeState = (raw) => {
    if (!raw) return '—';
    const s = String(raw).trim();
    if (!s) return '—';
    const up = s.toUpperCase();
    if (up === 'TX' || up === 'TEXAS') return 'TX';
    if (up === 'FL' || up === 'FLORIDA') return 'FL';
    // Return the raw 2-letter code if it's a valid abbreviation
    if (up.length === 2 && /^[A-Z]{2}$/.test(up)) return up;
    return '—';
  };

  const stageCount = (stageValue) => {
    const aliases = STAGE_ALIASES[stageValue] || [stageValue];
    return borrowers.filter(b => aliases.includes(b.application_status)).length;
  };

  return (
    <AdminLayout>
      {/* Sticky Filter Bar — two rows so the stage chips get the full
          horizontal space (single line, scrollable) and the search +
          partner filter sit cleanly above on their own line. */}
      <div className="admin-borrowers-filter-bar" style={{
        // Match the navbar's safe-area-aware height so the bar doesn't tuck
        // under the taller header (iOS desk PWA) and leave a grey gap.
        position: 'sticky', top: 'calc(64px + env(safe-area-inset-top, 0px))', zIndex: 100,
        backgroundColor: '#fff', borderBottom: '1px solid #e5e7eb',
        padding: '12px 32px', display: 'flex', flexDirection: 'column', gap: '10px',
        // 🔑 THE PAGE BACKGROUND IS GREY (#f9fafb on .admin-content) AND THIS BAR
        // IS WHITE, so any vertical gap between the navbar and this element
        // shows as a grey band. Jesse, on mobile: "there is a grey section
        // between the top nav bar and the search borrowers and filter bar".
        //
        // ⚠️ marginTop: -1px CLOSES THE SUB-PIXEL SEAM, not a layout gap. The
        // sticky `top` and the layout's paddingTop are the same expression, so
        // they agree exactly in CSS — but a fractional safe-area inset rounds
        // differently for a padding than for a sticky offset on iOS WebKit, and
        // the leftover is a 1px grey line across the full width. The border
        // below already separates the bar from the content, so losing one pixel
        // at the top costs nothing.
        marginTop: '-1px',
      }}>
        {/* Row 2: Multi-select Stage Pills — single line. flexWrap:nowrap
            with overflowX:auto so all 11 stages stay on one row at any
            width. Chips use stage.shortLabel for the longer stage names.
            Scrollbar visually hidden via .admin-borrowers-stage-chips
            below — chips remain swipeable on touch / scrollable via
            shift-wheel on desktop. */}
        <div style={{ position: 'relative', minWidth: 0, order: 2 }}>
        <div ref={chipsRef} onScroll={updateChipsFade} className="admin-borrowers-stage-chips" style={{
          display: 'flex', gap: '6px', flexWrap: 'nowrap', alignItems: 'center',
          overflowX: 'auto', minWidth: 0,
        }}>

          {/* All button */}
          <button
            onClick={handleClearStages}
            style={{
              padding: '5px 11px', borderRadius: '9999px', fontSize: '12px', fontWeight: '600',
              border: '2px solid', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
              backgroundColor: selectedStages.length === 0 ? '#3a6ad9' : '#fff',
              borderColor: selectedStages.length === 0 ? '#3a6ad9' : '#e5e7eb',
              color: selectedStages.length === 0 ? '#fff' : '#374151',
            }}
          >
            All ({borrowers.length})
          </button>

          {/* Stage pills */}
          {PIPELINE_STAGE_OPTIONS.map(stage => {
            const count = stageCount(stage.value);
            const isSelected = selectedStages.includes(stage.value);
            return (
              <button
                key={stage.value}
                onClick={() => handleStageToggle(stage.value)}
                title={stage.label}
                style={{
                  padding: '5px 11px', borderRadius: '9999px', fontSize: '12px',
                  fontWeight: isSelected ? '700' : '500',
                  border: '2px solid', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
                  display: 'inline-flex', alignItems: 'center', gap: '4px',
                  transition: 'all 0.15s ease',
                  backgroundColor: isSelected ? stage.color : '#fff',
                  borderColor: isSelected ? stage.color : '#e5e7eb',
                  color: isSelected ? stage.textColor : '#374151',
                }}
              >
                {isSelected && <span style={{ fontSize: '10px' }}>✓</span>}
                {stage.shortLabel || stage.label}
                <span style={{
                  background: isSelected ? 'rgba(0,0,0,0.15)' : '#f3f4f6',
                  color: isSelected ? stage.textColor : '#6b7280',
                  borderRadius: '9999px', padding: '0 5px', fontSize: '11px', fontWeight: '600',
                }}>
                  {count}
                </span>
              </button>
            );
          })}

          {/* Clear selection */}
          {selectedStages.length > 0 && (
            <button
              onClick={handleClearStages}
              style={{
                padding: '5px 10px', borderRadius: '9999px', fontSize: '12px',
                border: '1px solid #d1d5db', background: 'none', color: '#6b7280',
                cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '3px',
              }}
            >
              ✕ Clear ({selectedStages.length})
            </button>
          )}
        </div>
        {/* Right-edge fade — signals the pill row is horizontally scrollable. */}
        {chipsFade && (
          <div aria-hidden="true" style={{
            position: 'absolute', right: 0, top: 0, bottom: 0, width: 32,
            background: 'linear-gradient(to right, rgba(255,255,255,0), #ffffff)',
            pointerEvents: 'none',
          }} />
        )}
        </div>

        {/* Row 1: Search + Partner Filter (right-aligned, sits above chips) */}
        {/* ⚠️ CLASSED, NOT ADDRESSED BY DOM POSITION. The chips wrapper comes
            FIRST in the DOM and is placed second by `order`, so a
            `> div:first-of-type` rule reaches the chips and not this row — a
            selector that reads correct and styles the wrong element. */}
        <div className="admin-borrowers-search-row" style={{
          display: 'flex', gap: '10px', alignItems: 'center', justifyContent: 'flex-end',
          flexWrap: 'wrap', order: 1,
        }}>
          <div className="admin-borrowers-search-field" style={{ position: 'relative' }}>
            <svg style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 115 11a6 6 0 0112 0z" />
            </svg>
            <input
              type="text" placeholder="Search borrowers…" value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              style={{ height: '36px', paddingLeft: '32px', paddingRight: '12px', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '13px', outline: 'none', width: '240px' }}
            />
          </div>
          <select
            className="admin-partner-filter"
            value={partnerFilter}
            onChange={(e) => { setPartnerFilter(e.target.value); setPage(1); }}
            style={{ height: '36px', padding: '0 10px', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '13px', color: '#374151', backgroundColor: '#fff', cursor: 'pointer', outline: 'none' }}
          >
            <option value="">All Partners</option>
            {partnersList.map(p => (
              <option key={p.id} value={p.id}>
                {p.first_name} {p.last_name} ({p.partner_type === 'loan_officer' ? 'LO' : 'Realtor'})
              </option>
            ))}
          </select>
        </div>

        {/* Hide the horizontal scrollbar on the chip row — chips are
            scrollable via swipe on touch and shift-wheel on desktop, but
            the visible scrollbar was bleeding into the row above. */}
        <style>{`
          .admin-borrowers-stage-chips { scrollbar-width: none; -ms-overflow-style: none; }
          .admin-borrowers-stage-chips::-webkit-scrollbar { display: none; }

          /* ── 🚨 MOBILE: THE BAR IS TALLER AND THE PAGE IS NARROWER ────────
             Two symptoms, one cause. The bar stacks a search row over a chip
             row, so on a phone it is ~100px tall rather than ~84px — and the
             body below it kept a 24px top padding sized for the desktop bar,
             so the "Showing N Loans / Total Volume" strip sat under it.
             Jesse: "there needs to be more space/padding between the search
             borrowers and filter section and the loan count and volume count
             copy below it as right now it overlaps."

             ⚠️ THE SIDE PADDING IS THE OTHER HALF. 32px each side is 64px of a
             390px screen — a sixth of the width — which is what made the
             search field and the chips feel boxed in. 16px matches what the
             layout's own mobile block uses for tables. */
          @media (max-width: 768px) {
            .admin-borrowers-filter-bar { padding: 10px 16px !important; }
            .admin-borrowers-body { padding: 20px 16px !important; }
            /* 🔑 THE SEARCH FIELD FILLS THE ROW instead of sitting at a fixed
               240px with the partner select (hidden on mobile) beside it —
               right-aligned at 240px it left a dead gap to its left and read as
               a mis-centred control rather than a search bar. */
            .admin-borrowers-search-field { width: 100%; }
            .admin-borrowers-search-field input { width: 100% !important; }
          }
        `}</style>
      </div>

      {/* ⚠️ THE CLASS IS WHAT THE MEDIA QUERY REACHES. 32px of side padding is
          right on a desk monitor and most of a phone's width; the mobile block
          below takes it to 16px and widens the gap under the sticky bar, which
          is where the overlap was. */}
      <div className="admin-borrowers-body" style={{ padding: '24px 32px' }}>
        {/* Volume KPI strip */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: '24px',
          padding: '12px 20px', background: '#f9fafb', borderRadius: '10px',
          marginBottom: '16px', border: '1px solid #e5e7eb', flexWrap: 'wrap',
        }}>
          <div>
            <span style={{ fontSize: '12px', color: '#6b7280', display: 'block', marginBottom: '2px' }}>Showing</span>
            <span style={{ fontSize: '20px', fontWeight: '700', color: '#111827' }}>{filteredBorrowers.length} Loan{filteredBorrowers.length !== 1 ? 's' : ''}</span>
          </div>
          <div style={{ width: '1px', height: '36px', background: '#e5e7eb' }} />
          <div>
            <span style={{ fontSize: '12px', color: '#6b7280', display: 'block', marginBottom: '2px' }}>Total Volume</span>
            <span style={{ fontSize: '20px', fontWeight: '700', color: '#3a6ad9' }}>
              ${totalVolume.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
            </span>
          </div>
          {selectedStages.length > 0 && (
            <>
              <div style={{ width: '1px', height: '36px', background: '#e5e7eb' }} />
              <div>
                <span style={{ fontSize: '12px', color: '#6b7280', display: 'block', marginBottom: '4px' }}>Filtered by</span>
                <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                  {selectedStages.map(s => {
                    const stageConfig = PIPELINE_STAGE_OPTIONS.find(o => o.value === s);
                    return (
                      <span key={s} style={{
                        padding: '2px 8px', borderRadius: '9999px', fontSize: '11px', fontWeight: '600',
                        background: stageConfig?.color || '#f3f4f6',
                        color: stageConfig?.textColor || '#374151',
                      }}>
                        {stageConfig?.label || s}
                      </span>
                    );
                  })}
                </div>
              </div>
            </>
          )}
          <div className="admin-borrowers-cog-section" style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '12px' }}>
            {/* ⚠️ Say so on the page, not just in the menu. The borrower count
                sits right here, and a count quietly including reviewer rows is
                the drift this flag exists to prevent. */}
            {/* ⚠️ VISIBLE, NOT BURIED IN THE COG. This was only reachable through
                the ⚙️ Pipeline Settings menu, which is labelled with an icon and
                nothing else — someone looking for it went and could not find it.
                A filter that cannot be found is a filter that does not exist, so
                it now sits in the toolbar beside the borrower count with the
                other controls. The cog entry stays for muscle memory. */}
            <button
              onClick={() => setShowTestAccounts((v) => !v)}
              title={showTestAccounts
                ? 'Hide App Store reviewer and internal test accounts'
                : 'Show App Store reviewer and internal test accounts'}
              style={{
                padding: '5px 11px', fontSize: '12px', fontWeight: 700, cursor: 'pointer',
                borderRadius: '6px', whiteSpace: 'nowrap',
                background: showTestAccounts ? '#fef3c7' : '#fff',
                border: `1px solid ${showTestAccounts ? '#fde68a' : '#e5e7eb'}`,
                color: showTestAccounts ? '#92400e' : '#6b7280',
              }}
            >
              🧪 {showTestAccounts ? 'Test accounts shown' : 'Show test accounts'}
            </button>
            <span style={{ fontSize: '13px', color: '#6b7280' }}>{filteredBorrowers.length} borrower{filteredBorrowers.length !== 1 ? 's' : ''}</span>
            <div ref={pipelineCogRef} style={{ position: 'relative' }}>
              <button onClick={() => setShowPipelineCog(p => !p)}
                style={{ padding: '6px 10px', background: showPipelineCog ? '#f3f4f6' : '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', cursor: 'pointer', fontSize: '15px', color: '#6b7280', lineHeight: 1 }}
                title="Pipeline Settings">⚙️</button>
              {showPipelineCog && (
                <div style={{ position: 'absolute', top: 'calc(100% + 6px)', right: 0, background: '#fff', border: '1px solid #e5e7eb', borderRadius: '10px', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', minWidth: '200px', zIndex: 500, overflow: 'hidden' }}>
                  <div style={{ padding: '8px 14px', fontSize: '11px', color: '#9ca3af', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid #f3f4f6' }}>File Archives</div>
                  <button onClick={async () => {
                    setShowPipelineCog(false);
                    try {
                      const res = await axios.get(`${API}/api/admin/borrowers/archived`, { headers: { Authorization: `Bearer ${token}` } });
                      setArchivedLoans(res.data.borrowers || []); setShowArchivedModal(true);
                    } catch (e) { alert('Error: ' + e.message); }
                  }} style={{ width: '100%', padding: '10px 16px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', fontSize: '13px', color: '#374151', display: 'flex', alignItems: 'center', gap: '8px', borderBottom: '1px solid #f3f4f6' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'} onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                    📦 Archived Files
                  </button>
                  <button onClick={async () => {
                    setShowPipelineCog(false);
                    try {
                      const res = await axios.get(`${API}/api/admin/borrowers/adversed`, { headers: { Authorization: `Bearer ${token}` } });
                      setAdversedLoans(res.data.borrowers || []); setShowAdversedModal(true);
                    } catch (e) { alert('Error: ' + e.message); }
                  }} style={{ width: '100%', padding: '10px 16px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', fontSize: '13px', color: '#374151', display: 'flex', alignItems: 'center', gap: '8px' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'} onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                    ⛔ Adversed Files
                  </button>
                  {/* ⚠️ Test accounts are hidden by default. This toggle is the
                      only way to see them, and it is not persisted — a sticky
                      switch would leave someone reading an inflated pipeline
                      weeks later without knowing why the numbers moved. */}
                  <div style={{ padding: '8px 14px', fontSize: '11px', color: '#9ca3af', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em', borderTop: '1px solid #f3f4f6', borderBottom: '1px solid #f3f4f6' }}>Test Accounts</div>
                  <button onClick={() => { setShowTestAccounts(v => !v); setShowPipelineCog(false); }}
                    style={{ width: '100%', padding: '10px 16px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', fontSize: '13px', color: '#374151', display: 'flex', alignItems: 'center', gap: '8px' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'} onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                    {showTestAccounts ? '🙈 Hide test accounts' : '🧪 Show test accounts'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
        {/* Pending Partner Referrals — MOVED TO THE LEADS PAGE.
            These are buyer_referrals with no users row: people a partner
            invited who have not registered. They are pre-application by
            definition, so they belong with the other leads rather than in the
            applicant pipeline — and they already appear there as
            "Partner invite" origins with the partner named inline.
            The resend and archive actions moved with them; deleting the card
            without them would have stranded both. See AdminLeadCaptures.jsx. */}

        {/* Table */}
        <div style={{ backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #f3f4f6', overflow: 'auto', WebkitOverflowScrolling: 'touch', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '800px' }}>
            <thead>
              <tr style={{ backgroundColor: '#f9fafb' }}>
                {[
                  { label: 'Loan #', col: 'loan_number' },
                  { label: 'Borrower', col: 'last_name' },
                  { label: 'State', col: null },
                  { label: 'Loan Status', col: 'application_status' },
                  { label: 'Timeline', col: null },
                  { label: 'Stage', col: null },
                  { label: 'Purchase Price', col: 'purchase_price' },
                  { label: 'FICO', col: null },
                  { label: 'Referral Partner', col: null },
                  { label: 'Last Activity', col: 'last_seen_at' },
                ].map(({ label, col }) => (
                  <th key={label}
                    onClick={() => col && handleSort(col)}
                    style={{
                      padding: '11px 16px', textAlign: 'left', fontSize: '11px', fontWeight: '600',
                      color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em',
                      borderBottom: '1px solid #f3f4f6', cursor: col ? 'pointer' : 'default',
                      userSelect: 'none', whiteSpace: 'nowrap',
                    }}>
                    {label}{col && <SortIcon col={col} />}
                    {label === 'Stage' && (
                      <span
                        title="Stage — where the borrower currently is in their home-purchase journey. It's their own self-reported answer to 'Where are you in the home-buying process?' (e.g. Just Exploring, Actively Shopping, Home Chosen, Under Contract). Separate from Loan Status, which is the admin-driven loan pipeline stage."
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          marginLeft: 5, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          width: 13, height: 13, borderRadius: '50%', border: '1px solid #9ca3af',
                          color: '#9ca3af', fontSize: '9px', fontWeight: 700, fontStyle: 'italic',
                          cursor: 'help', verticalAlign: 'middle', textTransform: 'none',
                        }}
                      >i</span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan="10" style={{ padding: '32px', textAlign: 'center', color: '#9ca3af', fontSize: '13px' }}>Loading…</td></tr>
              )}
              {!loading && fetchError && (
                <tr><td colSpan="10" style={{ padding: '32px', textAlign: 'center', color: '#ff082f', fontSize: '13px' }}>⚠️ {fetchError}</td></tr>
              )}
              {!loading && !fetchError && filteredBorrowers.length === 0 && (
                <tr><td colSpan="10" style={{ padding: '32px', textAlign: 'center', color: '#9ca3af', fontSize: '13px' }}>No borrowers found</td></tr>
              )}
              {!loading && filteredBorrowers.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE).map((b) => {
                const state = normalizeState(b.state);
                const sc = stateColors[state];
                return (
                  <tr key={b.id}
                    onClick={() => navigate(`/admin/borrowers/${b.id}`)}
                    style={{ cursor: 'pointer', borderBottom: '1px solid #f9fafb' }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fafafa'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = ''}
                  >
                    <td style={{ padding: '13px 16px', fontSize: '12px', color: '#6b7280', fontFamily: 'monospace' }}>{b.loan_number || '—'}</td>
                    <td style={{ padding: '13px 16px' }}>
                      <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>{b.first_name} {b.last_name}</div>
                      <div style={{ fontSize: '11px', color: '#9ca3af' }}>{b.email}</div>
                    </td>
                    <td style={{ padding: '13px 16px' }}>
                      {sc ? (
                        <span style={{ padding: '2px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600', background: sc.bg, color: sc.color }}>
                          {state}
                        </span>
                      ) : (
                        <span style={{ fontSize: '12px', color: '#d1d5db' }}>—</span>
                      )}
                    </td>
                    <td style={{ padding: '13px 16px' }}><StatusBadge status={b.application_status} /></td>
                    <td style={{ padding: '13px 16px', fontSize: '13px', color: '#374151', whiteSpace: 'nowrap' }}>
                      {formatTimeline(b.timeline)}
                    </td>
                    <td style={{ padding: '13px 16px', whiteSpace: 'nowrap' }}>
                      {(() => {
                        // Plain-text rendering — color pill removed. Same
                        // short-form labels as the prior pill (Just exploring →
                        // Exploring, etc.) so the column width stays consistent.
                        const STAGE_LABEL = {
                          'Just exploring':         'Exploring',
                          'Actively shopping':      'Shopping',
                          'Have a home picked out': 'Home Chosen',
                          'Under contract':         'Under Contract',
                        };
                        const raw = b.buying_stage;
                        if (!raw) return <span style={{ fontSize: '12px', color: '#d1d5db' }}>—</span>;
                        return (
                          <span style={{ fontSize: '13px', color: '#374151' }}>
                            {STAGE_LABEL[raw] || raw}
                          </span>
                        );
                      })()}
                    </td>
                    <td style={{ padding: '13px 16px', fontSize: '13px', color: '#374151' }}>
                      {(() => {
                        const s = b.application_status;
                        let price, label;
                        if (POST_ACCEPTANCE_STAGES.includes(s)) {
                          price = b.contract_purchase_price || b.doorly_purchase_price || b.purchase_price;
                          label = 'Contract';
                        } else if (HOME_SHOPPING_STAGES.includes(s)) {
                          const dp = parseFloat(b.doorly_purchase_price || 0);
                          price = dp > 0 ? Math.round(dp) : (b.atlas_max_purchase_price || b.purchase_price);
                          label = 'Selected';
                        } else if (['approved','conditional_approval'].includes(s) && b.smartestimate_accepted) {
                          const dp = parseFloat(b.doorly_purchase_price || 0);
                          price = dp > 0 ? Math.round(dp) : (b.atlas_max_purchase_price || b.purchase_price);
                          label = 'Selected';
                        } else if (['approved','conditional_approval'].includes(s) && b.atlas_price_locked) {
                          price = b.atlas_max_purchase_price || b.max_approval_amount || b.purchase_price;
                          label = 'Approved';
                        } else {
                          price = b.purchase_price;
                          label = 'Requested';
                        }
                        if (!price) return '—';
                        const badgeColors = { Contract: '#3a6ad9', Selected: '#111827', Approved: '#acf44e', Requested: '#e5e7eb' };
                        const badgeText = { Contract: '#fff', Selected: '#acf44e', Approved: '#1a1a1a', Requested: '#6b7280' };
                        return (
                          <span>
                            ${Number(price).toLocaleString()}
                            <span style={{ fontSize: '9px', fontWeight: '700', background: badgeColors[label], color: badgeText[label], padding: '1px 5px', borderRadius: '3px', marginLeft: '5px', verticalAlign: 'middle' }}>{label[0]}</span>
                          </span>
                        );
                      })()}
                    </td>
                    <td style={{ padding: '13px 16px', fontSize: '13px', whiteSpace: 'nowrap' }}>
                      {(() => {
                        const hasActual = b.fico_score_actual && b.fico_score_source && b.fico_score_source.startsWith('xactus');
                        const score = hasActual ? b.fico_score_actual : (b.credit_score || null);
                        const range = b.credit_score_range;
                        if (score) {
                          const color = score >= 700 ? '#2d6a0a' : score >= 620 ? '#7a5200' : '#ff082f';
                          return <span style={{ fontWeight: '700', color }}>{score}{hasActual && <span style={{ fontSize: '9px', fontWeight: '600', background: '#acf44e', color: '#1a1a1a', padding: '0 4px', borderRadius: '3px', marginLeft: '4px' }}>Q</span>}</span>;
                        }
                        if (range) return <span style={{ color: '#9ca3af' }}>{range.replace(/_/g, '-')}</span>;
                        return <span style={{ color: '#d1d5db' }}>—</span>;
                      })()}
                    </td>
                    <td style={{ padding: '13px 16px' }} onClick={(e) => e.stopPropagation()}>
                      {b.referral_partner_name ? (
                        <div>
                          <span
                            onClick={() => navigate(`/admin/partners/partners?partnerId=${b.referral_partner_id}`)}
                            style={{ fontSize: '13px', fontWeight: '600', color: '#3a6ad9', cursor: 'pointer', textDecoration: 'underline' }}
                          >
                            {b.referral_partner_name}
                          </span>
                          <span style={{
                            marginLeft: 6, fontSize: '10px', fontWeight: '600', padding: '2px 6px',
                            borderRadius: '9999px',
                            backgroundColor: b.referral_partner_type === 'loan_officer' ? '#edf1fb' : '#eff6ff',
                            color: '#3a6ad9',
                          }}>
                            {b.referral_partner_type === 'loan_officer' ? 'LO' : 'Realtor'}
                          </span>
                        </div>
                      ) : (
                        <span style={{ fontSize: '12px', color: '#d1d5db' }}>—</span>
                      )}
                    </td>
                    <td style={{ padding: '13px 16px', fontSize: '12px', color: '#9ca3af' }}>
                      {(() => {
                        const ts = b.last_seen_at || b.status_updated_at || b.created_at;
                        if (!ts) return '—';
                        const diff = Date.now() - new Date(ts).getTime();
                        const mins = Math.floor(diff / 60000);
                        if (mins < 1) return 'Just now';
                        if (mins < 60) return `${mins}m ago`;
                        const hrs = Math.floor(mins / 60);
                        if (hrs < 24) return `${hrs}h ago`;
                        const days = Math.floor(hrs / 24);
                        if (days < 30) return `${days}d ago`;
                        return new Date(ts).toLocaleDateString();
                      })()}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '12px', color: '#6b7280' }}>
              Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filteredBorrowers.length)} of {filteredBorrowers.length}
            </span>
            <div style={{ display: 'flex', gap: '6px' }}>
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}
                style={{ padding: '6px 14px', borderRadius: '8px', border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: '12px', fontWeight: '500', color: '#374151', opacity: page === 1 ? 0.4 : 1 }}>Previous</button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const p = Math.max(1, Math.min(totalPages - 4, page - 2)) + i;
                return (
                  <button key={p} onClick={() => setPage(p)}
                    style={{ padding: '6px 12px', borderRadius: '8px', border: '1px solid', cursor: 'pointer', fontSize: '12px', fontWeight: p === page ? '700' : '500', borderColor: p === page ? '#3a6ad9' : '#e5e7eb', backgroundColor: p === page ? '#eff4ff' : '#fff', color: p === page ? '#3a6ad9' : '#374151' }}>
                    {p}
                  </button>
                );
              })}
              <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                style={{ padding: '6px 14px', borderRadius: '8px', border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: '12px', fontWeight: '500', color: '#374151', opacity: page === totalPages ? 0.4 : 1 }}>Next</button>
            </div>
          </div>
        )}
      </div>
      {/* Archived Files Modal */}
      {showArchivedModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div style={{ background: '#fff', borderRadius: '14px', padding: '28px', maxWidth: '700px', width: '100%', maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxSizing: 'border-box' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px', flexShrink: 0 }}>
              <div>
                <h3 style={{ margin: '0 0 4px', fontSize: '17px', fontWeight: '700' }}>📦 Archived Files</h3>
                <p style={{ margin: 0, fontSize: '13px', color: '#6b7280' }}>{archivedLoans.length} archived</p>
              </div>
              <button onClick={() => setShowArchivedModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '20px', color: '#6b7280' }}>✕</button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto' }}>
              {archivedLoans.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#9ca3af' }}>No archived files.</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                    {['Borrower', 'Loan #', 'Archived', 'By', ''].map(h => (
                      <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: '11px', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase' }}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {archivedLoans.map(b => (
                      <tr key={b.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                        <td style={{ padding: '10px 14px' }}>
                          <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>{b.first_name} {b.last_name}</div>
                          <div style={{ fontSize: '11px', color: '#9ca3af' }}>{b.email}</div>
                        </td>
                        <td style={{ padding: '10px 14px', fontSize: '13px', color: '#374151' }}>{b.loan_number || '—'}</td>
                        <td style={{ padding: '10px 14px', fontSize: '12px', color: '#6b7280' }}>{b.archived_at ? new Date(b.archived_at).toLocaleDateString() : '—'}</td>
                        <td style={{ padding: '10px 14px', fontSize: '12px', color: '#6b7280' }}>{b.archived_by || '—'}</td>
                        <td style={{ padding: '10px 14px' }}>
                          <button onClick={() => { setShowArchivedModal(false); navigate(`/admin/borrowers/${b.id}`); }}
                            style={{ padding: '4px 10px', background: '#f3f4f6', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', color: '#374151' }}>View</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Adversed Files Modal */}
      {showAdversedModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div style={{ background: '#fff', borderRadius: '14px', padding: '28px', maxWidth: '700px', width: '100%', maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxSizing: 'border-box' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px', flexShrink: 0 }}>
              <div>
                <h3 style={{ margin: '0 0 4px', fontSize: '17px', fontWeight: '700' }}>⛔ Adversed Files</h3>
                <p style={{ margin: 0, fontSize: '13px', color: '#6b7280' }}>{adversedLoans.length} adversed</p>
              </div>
              <button onClick={() => setShowAdversedModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '20px', color: '#6b7280' }}>✕</button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto' }}>
              {adversedLoans.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#9ca3af' }}>No adversed files.</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                    {['Borrower', 'Loan #', 'Date', 'Reasons', ''].map(h => (
                      <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: '11px', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase' }}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {adversedLoans.map(b => {
                      let ad = {};
                      try { ad = typeof b.adverse_data === 'string' ? JSON.parse(b.adverse_data) : (b.adverse_data || {}); } catch (_) {}
                      const reasons = [ad.reason_credit && 'Credit', ad.reason_income && 'Income', ad.reason_employment && 'Employment', ad.reason_dti && 'DTI', ad.reason_ltv && 'LTV', ad.reason_property && 'Property', ad.reason_incomplete && 'Incomplete', ad.reason_other && 'Other'].filter(Boolean);
                      return (
                        <tr key={b.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                          <td style={{ padding: '10px 14px' }}>
                            <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>{b.first_name} {b.last_name}</div>
                            <div style={{ fontSize: '11px', color: '#9ca3af' }}>{b.email}</div>
                          </td>
                          <td style={{ padding: '10px 14px', fontSize: '13px', color: '#374151' }}>{b.loan_number || '—'}</td>
                          <td style={{ padding: '10px 14px', fontSize: '12px', color: '#6b7280' }}>{b.adversed_at ? new Date(b.adversed_at).toLocaleDateString() : '—'}</td>
                          <td style={{ padding: '10px 14px' }}>
                            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                              {reasons.map(r => <span key={r} style={{ fontSize: '10px', background: '#fff0f3', color: '#ff082f', padding: '1px 6px', borderRadius: '4px', fontWeight: '600' }}>{r}</span>)}
                            </div>
                          </td>
                          <td style={{ padding: '10px 14px' }}>
                            <button onClick={() => { setShowAdversedModal(false); navigate(`/admin/borrowers/${b.id}`); }}
                              style={{ padding: '4px 10px', background: '#f3f4f6', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', color: '#374151' }}>View</button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
